import TowersMap from '../components/towersMap.js';
import ThreeSequenceRenderer from '../components/ThreeSequenceRenderer.js';
require('./home-mobile/modules/contato-dropdown');
document.addEventListener('DOMContentLoaded', () => window.HomeContatoDropdown?.init());

/*
        SUMMARY
            0 - Const / Variables
            1 - Classes
            2 - Functions
            3 - Event Listeners
*/


/*---------------------/ 0 - Const - Variables /-------------------*/

let preloaderHidden = false;
const IS_MOBILE = window.innerWidth <= 768;

/**
 * Autoplay com fallback para Low Power Mode (iOS) e demais bloqueios de autoplay.
 *
 * Monitora a Promise de video.play(). Se o sistema rejeitar o autoplay
 * (ex.: modo de economia de energia do iPhone), o vídeo é mantido pausado
 * exibindo um frame estático (o "poster" do próprio vídeo) e a classe
 * `autoplay-fallback` é injetada no container da seção. A navegação do
 * slider continua normalmente — apenas o fundo deixa de se mover.
 *
 * @param {HTMLVideoElement} video
 */
function safeAutoplay(video) {
    if (!video) return;

    const playPromise = video.play();
    if (!playPromise || typeof playPromise.then !== 'function') return;

    playPromise
        .then(() => {
            // Autoplay aceito: garante que um fallback anterior seja removido
            const container = video.closest('section, .luxo, .galeria__slide, .arquitetura__image');
            if (container) container.classList.remove('autoplay-fallback');
            video.classList.remove('autoplay-fallback-video');
        })
        .catch(() => {
            applyAutoplayFallback(video);
        });
}

/**
 * Mantém o vídeo pausado exibindo um frame estático de alto impacto e
 * marca o container com a classe `autoplay-fallback`.
 *
 * @param {HTMLVideoElement} video
 */
function applyAutoplayFallback(video) {
    if (!video) return;

    const container = video.closest('section, .luxo, .galeria__slide, .arquitetura__image')
        || video.parentElement;
    if (container) container.classList.add('autoplay-fallback');
    video.classList.add('autoplay-fallback-video');

    // Garante que um frame seja decodificado e exibido como imagem estática.
    // O iOS renderiza o frame de um <video playsinline> pausado após o seek.
    const showFrame = () => {
        try {
            video.pause();
            if (video.currentTime < 0.05) {
                video.currentTime = 0.1;
            }
        } catch (e) {
            /* seek não suportado neste estado — ignora */
        }
    };

    if (video.readyState >= 2) {
        showFrame();
    } else {
        video.preload = 'auto';
        video.addEventListener('loadeddata', showFrame, { once: true });
        try {
            video.load();
        } catch (e) {
            /* load já em andamento — ignora */
        }
    }
}

/**
 * Performance optimization: Prioritized frame loading configuration
 */
const FRAME_LOADING_CONFIG = {
    planet: {
        initialCount: IS_MOBILE ? 50 : 100,
        batchSize: IS_MOBILE ? 10 : 20,
        batchDelay: IS_MOBILE ? 100 : 50,
        frameStep: 1
    },
    build: {
        initialCount: IS_MOBILE ? 25 : 50,
        batchSize: IS_MOBILE ? 8 : 15,
        batchDelay: IS_MOBILE ? 100 : 50,
        observerMargin: 500,
        frameStep: 1
    }
};


/*---------------------/ 1 - Classes /-------------------*/

/**
 * Gerencia o vídeo de introdução e animações
 */
class IntroVideoManager {
    constructor() {
        this.introVideo = document.getElementById('introVideo');
        this.introSection = document.querySelector('.intro-video');
        this.tagline = document.querySelector('.intro-video__tagline');
        this.introText = document.querySelector('.intro-video__text');
        this.scrollIcon = document.querySelector('.intro-video__scroll-icon');
        this.scrollOverlay = document.querySelector('.intro-video__scroll-overlay');
        this.header = document.querySelector('header');

        if (!this.introVideo || !this.introSection) {
            console.warn('Intro video elements not found');
            return;
        }

        const videoSrc = IS_MOBILE
            ? this.introVideo.dataset.srcMobile
            : this.introVideo.dataset.srcDesktop;
        if (videoSrc) {
            this.introVideo.src = videoSrc;
        }

        // Hide header initially
        if (this.header) {
            gsap.set(this.header, { yPercent: -100, opacity: 0 });
        }

        // Set initial state for elements
        if (this.tagline) {
            gsap.set(this.tagline, { y: 50, opacity: 0 });
        }

        if (this.introText) {
            gsap.set(this.introText, { y: 80, opacity: 0 });
        }

        if (this.scrollIcon) {
            gsap.set(this.scrollIcon, { y: 30, opacity: 0 });
        }

        this.videoLoaded = false;
        this.setupVideoLoadHandler();
        this.setupScrollLockEffect();
        this.setupStickyHeaderOnScroll();
    }

    setupVideoLoadHandler() {
        const onVideoLoaded = () => {
            this.videoLoaded = true;
            console.log('✅ Intro video loaded, duration:', this.introVideo.duration);
        };

        if (this.introVideo.readyState >= 1) {
            onVideoLoaded();
        } else {
            this.introVideo.addEventListener('loadedmetadata', onVideoLoaded, { once: true });
            this.introVideo.addEventListener('canplay', onVideoLoaded, { once: true });
        }
    }

    /**
     * Intro fica fixa no scroll; overlay escurece até preto; depois libera para text-transition.
     */
    setupScrollLockEffect() {
        if (!this.scrollOverlay || typeof ScrollTrigger === 'undefined') return;

        const scrollerConfig = window.lenis
            ? { scroller: document.documentElement }
            : {};

        const pinDistance = IS_MOBILE ? '25vh' : '25vh';
        const triggerConfig = {
            trigger: this.introSection,
            start: 'top top',
            end: `+=${pinDistance}`,
            scrub: true,
            invalidateOnRefresh: true,
            ...scrollerConfig
        };

        // Pin da intro enquanto o usuário scrolla
        ScrollTrigger.create({
            ...triggerConfig,
            pin: true,
            pinSpacing: true
        });

        // Overlay preto 0 → 1 (escurece até ficar totalmente preto)
        gsap.fromTo(
            this.scrollOverlay,
            { opacity: 0 },
            {
                opacity: 1,
                ease: 'power2.inOut',  // Mais suave e equilibrado (reduz tempo de tela preta)
                scrollTrigger: { ...triggerConfig }
            }
        );

        // Conteúdo da intro some junto com o escurecimento
        const contentElements = [this.tagline, this.introText, this.scrollIcon].filter(Boolean);
        if (contentElements.length) {
            gsap.to(contentElements, {
                opacity: 0,
                ease: 'power2.in',  // Suave: começa devagar, termina rápido (desaparece naturalmente)
                scrollTrigger: { ...triggerConfig }
            });
        }

        console.log('✅ Intro scroll-lock effect initialized');
    }

    /**
     * Após o usuário passar da intro, limpa estilos inline do GSAP para que
     * as classes scrolled-up/scrolled-down (do header.js) controlem a visibilidade.
     * Ao voltar para a intro, reaplica os estilos GSAP para manter o header visível.
     */
    setupStickyHeaderOnScroll() {
        if (!this.header || !this.introSection || typeof ScrollTrigger === 'undefined') return;

        const header = this.header;

        const scrollerConfig = window.lenis
            ? { scroller: document.documentElement }
            : {};

        ScrollTrigger.create({
            trigger: this.introSection,
            start: 'bottom top',
            ...scrollerConfig,
            onEnter: () => {
                // Limpa estilos inline do GSAP para que as classes CSS funcionem
                // (scrolled-down já está aplicado pelo header.js ao scrollar pra baixo)
                gsap.set(header, { clearProps: 'transform,opacity' });
            },
            onLeaveBack: () => {
                // Voltou para a intro — reaplica estilos GSAP para manter o header visível
                header.classList.remove('scrolled-down', 'scrolled-up');
                gsap.set(header, { yPercent: 0, opacity: 1 });
            }
        });

        console.log('✅ Sticky header scroll trigger initialized');
    }

    startIntroSequence() {
        if (!this.videoLoaded) {
            console.warn('Intro video not loaded yet');
            return;
        }

        // Start playing the video
        this.introVideo.play().catch(err => {
            console.error('Error playing intro video:', err);
        });

        // Create GSAP timeline for animations
        const tl = gsap.timeline({ delay: 3 });

        // Animate tagline first
        if (this.tagline) {
            tl.to(this.tagline, {
                y: 0,
                opacity: 1,
                duration: 1,
                ease: 'power2.out'
            });
        }

        // Animate main text
        if (this.introText) {
            tl.to(this.introText, {
                y: 0,
                opacity: 1,
                duration: 1.2,
                ease: 'power2.out'
            }, '-=0.6'); // Start 0.6s before previous animation ends
        }

        // Animate scroll icon
        if (this.scrollIcon) {
            tl.to(this.scrollIcon, {
                y: 0,
                opacity: 1,
                duration: 0.8,
                ease: 'power2.out'
            }, '-=0.8');
        }

        // Animate header from top to bottom
        if (this.header) {
            tl.to(this.header, {
                yPercent: 0,
                opacity: 1,
                duration: 0.8,
                ease: 'power2.out'
            }, '-=0.6');
        }

        console.log('✅ Intro sequence started');
    }
}

/**
 * Gerencia a sequência de imagens do planeta com textos que aparecem e somem
 */
class PlanetSequenceManager {
    constructor() {
        this.canvas = document.querySelector('.planet-sequence__canvas');
        this.wrapper = document.querySelector('.planet-sequence');
        this.spacer = document.querySelector('.planet-sequence__spacer');
        this.textSection = document.querySelector('.planet-sequence__text-section');
        this.textOverlay = document.querySelector('.planet-sequence__text-overlay');
        this.textContent = document.querySelector('.planet-sequence__text-content');
        this.texts = document.querySelectorAll('.planet-sequence__text');

        // Info overlay elements
        this.infoOverlay = document.querySelector('.planet-sequence__info-overlay');
        this.bottomCard = document.querySelector('.planet-sequence__bottom-card');
        this.infoShown = false;
        this.infoControlledByBuild = false; // Flag para quando BuildSequence controla o info overlay

        if (!this.canvas || !this.wrapper) {
            console.warn('PlanetSequenceManager: Required elements not found');
            return;
        }

        if (IS_MOBILE) {
            this.renderer = new ThreeSequenceRenderer(this.canvas);
        } else {
            this.context = this.canvas.getContext('2d');
        }

        const framePath = IS_MOBILE
            ? (this.canvas.dataset.framePathMobile || this.canvas.dataset.framePath)
            : this.canvas.dataset.framePath;
        this.frameCount = parseInt(
            IS_MOBILE
                ? (this.canvas.dataset.frameCountMobile || this.canvas.dataset.frameCount)
                : this.canvas.dataset.frameCount,
            10
        );

        this.currentFrameSrc = (index) =>
            `${framePath}/img_${(index + 1).toString().padStart(4, '0')}.avif`;

        this.images = [];
        this.currentFrame = 0;
        this.loaded = false;
        this.initialFramesLoaded = false;
        this.initialFramesPromise = null;

        if (!IS_MOBILE) {
            this.setCanvasSize();
        }
        this.preloadImages();

        window.addEventListener('resize', () => {
            if (IS_MOBILE) {
                this.renderer.resize();
            } else {
                this.setCanvasSize();
            }
            this.render();
        });
    }

    setCanvasSize() {
        const pixelRatio = window.devicePixelRatio || 1;
        this.canvas.width = window.innerWidth * pixelRatio;
        this.canvas.height = window.innerHeight * pixelRatio;
        this.canvas.style.width = window.innerWidth + 'px';
        this.canvas.style.height = window.innerHeight + 'px';
        this.context.scale(pixelRatio, pixelRatio);
    }

    /**
     * Retorna Promise que resolve quando os primeiros N frames estão carregados
     * @param {number} count - Número de frames iniciais a aguardar (default: 100)
     */
    getInitialFramesPromise(count = 100) {
        if (this.initialFramesLoaded) {
            return Promise.resolve();
        }
        if (this.initialFramesPromise) {
            return this.initialFramesPromise;
        }
        // Se ainda não começou o preload, cria uma Promise que será resolvida no preloadImages
        this.initialFramesPromise = new Promise((resolve) => {
            this._resolveInitialFrames = resolve;
            this._initialFrameTarget = Math.min(count, this.frameCount);
        });
        return this.initialFramesPromise;
    }

    /**
     * Retorna Promise que resolve quando TODOS os frames estão carregados
     */
    getAllFramesPromise() {
        if (this.loaded) return Promise.resolve();
        if (this._allFramesPromise) return this._allFramesPromise;
        this._allFramesPromise = new Promise((resolve) => {
            this._resolveAllFrames = resolve;
        });
        return this._allFramesPromise;
    }

    /**
     * Preloads frames with prioritized loading:
     * 1. Load initial batch (for preloader)
     * 2. Continue loading remaining frames in batches after preloader hides
     */
    preloadImages() {
        const config = FRAME_LOADING_CONFIG.planet;
        this.frameStep = config.frameStep;
        this.totalLoaded = 0;
        this.loadingQueue = [];
        this.isBackgroundLoading = false;

        // Build list of frames to load (with step)
        this.framesToLoad = [];
        for (let i = 0; i < this.frameCount; i += this.frameStep) {
            this.framesToLoad.push(i);
        }
        this.effectiveFrameCount = this.framesToLoad.length;

        const initialFrameTarget = this._initialFrameTarget
            ? Math.min(this._initialFrameTarget, this.effectiveFrameCount)
            : Math.min(config.initialCount, this.effectiveFrameCount);

        // Create placeholder array for all images
        for (let i = 0; i < this.frameCount; i++) {
            this.images.push(null);
        }

        // Prepara a Promise dos frames iniciais se ainda não foi criada
        if (!this.initialFramesPromise) {
            this.getInitialFramesPromise(initialFrameTarget);
        }

        // Load initial frames immediately (for preloader)
        console.log(`🔄 PlanetSequence: Loading initial ${initialFrameTarget} frames (step: ${this.frameStep})...`);
        for (let i = 0; i < initialFrameTarget; i++) {
            this.loadFrame(this.framesToLoad[i], true);
        }

        // Queue remaining frames for background loading
        for (let i = initialFrameTarget; i < this.effectiveFrameCount; i++) {
            this.loadingQueue.push(this.framesToLoad[i]);
        }
    }

    /**
     * Load a single frame
     * @param {number} index - Frame index to load
     * @param {boolean} isInitial - Whether this is part of initial batch
     */
    loadFrame(index, isInitial = false, retryCount = 0) {
        const MAX_RETRIES = 3;
        if (retryCount === 0 && this.images[index] !== null) return; // Already loaded or loading

        const img = new Image();
        this.images[index] = img; // Mark as loading

        img.onload = () => this.onFrameLoaded(index, isInitial);
        img.onerror = () => {
            if (retryCount < MAX_RETRIES) {
                const delay = Math.pow(2, retryCount) * 1000;
                setTimeout(() => {
                    this.images[index] = null;
                    this.loadFrame(index, isInitial, retryCount + 1);
                }, delay);
            } else {
                this.onFrameLoaded(index, isInitial);
            }
        };
        img.src = this.currentFrameSrc(index);
    }

    /**
     * Handle frame load completion
     */
    onFrameLoaded(index, isInitial) {
        this.totalLoaded++;
        const config = FRAME_LOADING_CONFIG.planet;

        // Resolve Promise dos frames iniciais quando atingir o target
        if (!this.initialFramesLoaded && this.totalLoaded >= Math.min(config.initialCount, this.effectiveFrameCount)) {
            this.initialFramesLoaded = true;
            if (this._resolveInitialFrames) {
                this._resolveInitialFrames();
                console.log(`✅ PlanetSequence: Initial ${config.initialCount} frames loaded (step: ${this.frameStep})`);
            }
        }

        // Quando todos carregam, configura animações
        if (this.totalLoaded >= this.effectiveFrameCount) {
            this.loaded = true;
            if (this._resolveAllFrames) this._resolveAllFrames();
            this.render();
            this.setupScrollAnimations();
            console.log(`✅ PlanetSequence: All ${this.effectiveFrameCount} frames loaded (of ${this.frameCount} total)`);
        }
    }

    /**
     * Start background loading of remaining frames (called after preloader hides)
     */
    startBackgroundLoading() {
        if (this.isBackgroundLoading || this.loadingQueue.length === 0) return;
        this.isBackgroundLoading = true;

        const config = FRAME_LOADING_CONFIG.planet;
        console.log(`🔄 PlanetSequence: Starting background loading of ${this.loadingQueue.length} remaining frames...`);

        const loadBatch = () => {
            if (this.loadingQueue.length === 0) {
                console.log('✅ PlanetSequence: Background loading complete');
                return;
            }

            // Load a batch of frames
            const batch = this.loadingQueue.splice(0, config.batchSize);
            batch.forEach(index => this.loadFrame(index, false));

            // Schedule next batch
            if (this.loadingQueue.length > 0) {
                setTimeout(loadBatch, config.batchDelay);
            }
        };

        // Start loading batches
        loadBatch();
    }

    render() {
        let frameIndex = this.currentFrame;
        if (this.frameStep > 1) {
            frameIndex = Math.round(frameIndex / this.frameStep) * this.frameStep;
            frameIndex = Math.min(frameIndex, this.frameCount - 1);
        }

        const img = this.images[frameIndex];
        if (!img || !img.complete || !img.naturalWidth) return;

        if (IS_MOBILE) {
            this.renderer.updateFrame(img);
        } else {
            const canvasWidth = window.innerWidth;
            const canvasHeight = window.innerHeight;

            this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);

            const imageAspect = img.naturalWidth / img.naturalHeight;
            const canvasAspect = canvasWidth / canvasHeight;

            let drawWidth, drawHeight, drawX, drawY;

            if (imageAspect > canvasAspect) {
                drawHeight = canvasHeight;
                drawWidth = drawHeight * imageAspect;
                drawX = (canvasWidth - drawWidth) / 2;
                drawY = 0;
            } else {
                drawWidth = canvasWidth;
                drawHeight = drawWidth / imageAspect;
                drawX = 0;
                drawY = (canvasHeight - drawHeight) / 2;
            }

            this.context.drawImage(img, drawX, drawY, drawWidth, drawHeight);
        }
    }

    /**
     * Mostra o overlay de informações sobre o último frame
     */
    showInfoOverlay() {
        if (this.infoShown || !this.infoOverlay || this.infoControlledByBuild) return;
        this.infoShown = true;

        // Kill any running info timeline
        this.infoTimeline?.kill();

        // Ativa o overlay
        this.infoOverlay.classList.add('active');

        if (IS_MOBILE) {
            // Mobile: Fase 1 — pin/contour/blob aparecem imediatamente (animado)
            this.infoTimeline = gsap.timeline()
                .to(this.infoOverlay, { opacity: 1, duration: 0.4 }, 0)
                .to('.planet-sequence__contour', { opacity: 1, duration: 0.4 }, 0)
                .to('.planet-sequence__pin', { opacity: 1, scale: 1, duration: 0.4, ease: 'back.out(1.7)' }, 0)
                .to('.planet-sequence__blob', { opacity: 1, y: 0, duration: 0.4 }, 0);

            // Fase 2: scroll-driven — pin/contour/blob somem e bottom-card aparece
            const lockSpacer = document.querySelector('.planet-sequence__info-lock-spacer');
            if (lockSpacer) {
                this._mobileInfoScrollHandler = () => {
                    const rect = lockSpacer.getBoundingClientRect();
                    const lockHeight = rect.height;
                    // Progresso: 0 quando topo do spacer está no bottom da viewport, 1 quando bottom do spacer sai
                    const scrolledInto = window.innerHeight - rect.top;
                    const progress = Math.max(0, Math.min(scrolledInto / lockHeight, 1));

                    // 0–30% do lock-spacer: fade out pin/contour/blob
                    const FADE_END = 0.30;
                    if (progress <= FADE_END) {
                        const fadeProgress = progress / FADE_END; // 0→1
                        gsap.set('.planet-sequence__contour', { opacity: 1 - fadeProgress });
                        gsap.set('.planet-sequence__pin', { opacity: 1 - fadeProgress, scale: 1 - fadeProgress });
                        gsap.set('.planet-sequence__blob', { opacity: 1 - fadeProgress, y: fadeProgress * 10 });

                        if (fadeProgress >= 1) {
                            this.bottomCard?.classList.add('active');
                        } else {
                            this.bottomCard?.classList.remove('active');
                        }
                    } else {
                        // Já passou — manter estado final
                        gsap.set('.planet-sequence__contour', { opacity: 0 });
                        gsap.set('.planet-sequence__pin', { opacity: 0, scale: 0 });
                        gsap.set('.planet-sequence__blob', { opacity: 0, y: 10 });
                        this.bottomCard?.classList.add('active');
                    }
                };

                if (window.lenis) {
                    window.lenis.on('scroll', this._mobileInfoScrollHandler);
                } else {
                    document.addEventListener('scroll', this._mobileInfoScrollHandler);
                }
            }
        } else {
            // Desktop: tudo simultâneo
            this.infoTimeline = gsap.timeline()
                .to(this.infoOverlay, { opacity: 1, duration: 0.4 }, 0)
                .to('.planet-sequence__contour', { opacity: 1, duration: 0.4 }, 0)
                .to('.planet-sequence__pin', { opacity: 1, scale: 1, duration: 0.4, ease: 'back.out(1.7)' }, 0)
                .to('.planet-sequence__blob', { opacity: 1, y: 0, duration: 0.4 }, 0)
                .add(() => this.bottomCard?.classList.add('active'), 0);
        }

        console.log('🎯 Info overlay shown at frame', this.currentFrame);
    }

    /**
     * Esconde o overlay de informações (ao scrollar para cima)
     */
    hideInfoOverlay() {
        if (!this.infoShown || !this.infoOverlay || this.infoControlledByBuild) return;
        this.infoShown = false;

        // Kill any running info timeline
        this.infoTimeline?.kill();

        // Remove o scroll handler mobile se existir
        if (IS_MOBILE && this._mobileInfoScrollHandler) {
            if (window.lenis) {
                window.lenis.off('scroll', this._mobileInfoScrollHandler);
            } else {
                document.removeEventListener('scroll', this._mobileInfoScrollHandler);
            }
            this._mobileInfoScrollHandler = null;
        }

        // Immediately set hidden state so elements don't linger on fast scroll
        this.bottomCard?.classList.remove('active');
        gsap.set('.planet-sequence__blob', { opacity: 0, y: 10 });
        gsap.set('.planet-sequence__pin', { opacity: 0, scale: 0 });
        gsap.set('.planet-sequence__contour', { opacity: 0 });
        gsap.set(this.infoOverlay, { opacity: 0 });
        this.infoOverlay.classList.remove('active');

        console.log('🎯 Info overlay hidden at frame', this.currentFrame);
    }

    setupScrollAnimations() {
        const scrollerConfig = window.lenis ? { scroller: document.documentElement } : {};

        // Animação dos textos - aparecem sequencialmente na primeira parte
        // Ajustado: textos aparecem mais cedo para reduzir tempo de tela preta
        if (this.texts.length && this.textSection) {
            this.texts.forEach((text, index) => {
                const startPercentage = 1 + (index * 10); // Reduzido de 3 + (index * 13)
                const endPercentage = 6 + (index * 10);   // Reduzido de 9 + (index * 13)

                gsap.fromTo(text,
                    { opacity: 0, y: 30 },
                    {
                        opacity: 1,
                        y: 0,
                        scrollTrigger: {
                            trigger: this.textSection,
                            start: `${startPercentage}% 25%`,
                            end: `${endPercentage}% 25%`,
                            scrub: 1,
                            ...scrollerConfig
                        }
                    }
                );
            });
        }

        // Overlay fade out - começa a revelar a sequência mais cedo (com textos ainda visíveis)
        // Ajustado: fade out começa antes para reduzir tempo de tela preta
        if (this.textOverlay && this.textSection) {
            gsap.fromTo(this.textOverlay,
                { opacity: 1 },
                {
                    opacity: 0,
                    scrollTrigger: {
                        trigger: this.textSection,
                        start: '8% top',
                        end: '30% top',
                        scrub: 1,
                        ...scrollerConfig
                    }
                }
            );
        }

        // Setup scroll para a sequência de imagens (começa junto com o fade do overlay)
        // Os textos somem baseado no frame atual (40-50)
        this.setupSequenceScroll();
    }

    setupSequenceScroll() {
        if (!this.spacer || !this.textSection) {
            console.warn('PlanetSequenceManager: Required elements not found');
            return;
        }

        let top, bottom;
        const FRAME_TO_HIDE_TEXT = Math.round(this.frameCount * 0.10);
        const FRAME_TO_SHOW_INFO = Math.round(this.frameCount * 0.99);

        const updatePositions = () => {
            // Início: 60% da text-section (junto com o fade do overlay)
            const textSectionRect = this.textSection.getBoundingClientRect();
            const textSectionTop = textSectionRect.top + window.scrollY;
            const textSectionHeight = textSectionRect.height;

            // Fim: final do spacer
            const spacerRect = this.spacer.getBoundingClientRect();

            top = textSectionTop + (textSectionHeight * 0.10); // Começa em 10% da text-section
            bottom = spacerRect.bottom - window.innerHeight + window.scrollY;

            console.log('🎬 Planet Sequence positions:', { top, bottom, scrollY: window.scrollY });
        };

        updatePositions();

        // Controle de estado dos textos
        let textsFadedOut = false;

        const fadeOutTexts = () => {
            if (textsFadedOut || !this.textContent) return;
            textsFadedOut = true;

            gsap.to(this.textContent, {
                opacity: 0,
                duration: 0.8,
                ease: 'power2.out'
            });
            console.log('🎬 Textos fazendo fade out no frame', this.currentFrame);
        };

        const fadeInTexts = () => {
            if (!textsFadedOut || !this.textContent) return;
            textsFadedOut = false;

            gsap.to(this.textContent, {
                opacity: 1,
                duration: 0.5,
                ease: 'power2.out'
            });
            console.log('🎬 Textos fazendo fade in no frame', this.currentFrame);
        };

        const onScroll = (scrollY) => {
            // Calcula progresso baseado no range (60% text-section até fim do spacer)
            const progress = Math.max(Math.min((scrollY - top) / (bottom - top), 1), 0);
            const targetFrame = Math.round(progress * (this.frameCount - 1));

            // Debug: mostra frame atual e progresso
            // console.log(`🖼️ Frame: ${targetFrame + 1}/${this.frameCount} | Progress: ${(progress * 100).toFixed(1)}% | ScrollY: ${scrollY.toFixed(0)} | Top: ${top.toFixed(0)}`);

            if (targetFrame !== this.currentFrame) {
                this.currentFrame = targetFrame;
                this.render();

                // Verifica se chegou no frame para esconder os textos
                if (this.currentFrame >= FRAME_TO_HIDE_TEXT) {
                    fadeOutTexts();
                } else {
                    // Scrollou para cima - mostra os textos novamente
                    fadeInTexts();
                }

                // Verifica se chegou no frame para mostrar o info overlay
                if (this.currentFrame >= FRAME_TO_SHOW_INFO) {
                    this.showInfoOverlay();
                } else {
                    // Scrollou para cima - esconde o info overlay
                    this.hideInfoOverlay();
                }
            }
        };

        if (window.lenis) {
            window.lenis.on('scroll', ({ scroll }) => onScroll(scroll));
        } else {
            document.addEventListener('scroll', () => onScroll(window.scrollY));
        }

        window.addEventListener('resize', updatePositions);

        // Initial render at current scroll position
        onScroll(window.scrollY);
    }
}


/**
 * Gerencia a segunda sequência de imagens (build) que continua após o planet-sequence
 */
class BuildSequenceManager {
    constructor(planetManager) {
        this.planetManager = planetManager;
        this.canvas = document.querySelector('.build-sequence__canvas');
        this.spacer = document.querySelector('.build-sequence__spacer');
        this.lockSpacer = document.querySelector('.planet-sequence__info-lock-spacer');
        this.planetInfoOverlay = document.querySelector('.planet-sequence__info-overlay');
        this.planetBottomCard = document.querySelector('.planet-sequence__bottom-card');
        this.finalOverlay = document.querySelector('.build-sequence__final-overlay');
        this.columns = document.querySelectorAll('.build-sequence__column');

        if (!this.canvas || !this.spacer) {
            console.warn('BuildSequenceManager: Required elements not found');
            return;
        }

        if (IS_MOBILE) {
            this.renderer = new ThreeSequenceRenderer(this.canvas);
        } else {
            this.context = this.canvas.getContext('2d');
        }

        const framePath = IS_MOBILE
            ? (this.canvas.dataset.framePathMobile || this.canvas.dataset.framePath)
            : this.canvas.dataset.framePath;
        this.frameCount = parseInt(
            IS_MOBILE
                ? (this.canvas.dataset.frameCountMobile || this.canvas.dataset.frameCount)
                : this.canvas.dataset.frameCount,
            10
        );

        this.currentFrameSrc = (index) =>
            `${framePath}/img_${(index + 1).toString().padStart(4, '0')}.avif`;

        this.images = [];
        this.currentFrame = 0;
        this.loaded = false;
        this.isActive = false;
        this.columnsRevealed = false;
        this.initialFramesLoaded = false;
        this.initialFramesPromise = null;

        if (!IS_MOBILE) {
            this.setCanvasSize();
        }
        this.preloadImages();

        window.addEventListener('resize', () => {
            if (IS_MOBILE) {
                this.renderer.resize();
            } else {
                this.setCanvasSize();
            }
            if (this.isActive) {
                this.render();
            }
        });
    }

    setCanvasSize() {
        const pixelRatio = window.devicePixelRatio || 1;
        this.canvas.width = window.innerWidth * pixelRatio;
        this.canvas.height = window.innerHeight * pixelRatio;
        this.canvas.style.width = window.innerWidth + 'px';
        this.canvas.style.height = window.innerHeight + 'px';
        this.context.scale(pixelRatio, pixelRatio);
    }

    /**
     * Retorna Promise que resolve quando os primeiros N frames estão carregados
     * @param {number} count - Número de frames iniciais a aguardar (default: 50)
     */
    getInitialFramesPromise(count = 50) {
        if (this.initialFramesLoaded) {
            return Promise.resolve();
        }
        if (this.initialFramesPromise) {
            return this.initialFramesPromise;
        }
        this.initialFramesPromise = new Promise((resolve) => {
            this._resolveInitialFrames = resolve;
            this._initialFrameTarget = Math.min(count, this.frameCount);
        });
        return this.initialFramesPromise;
    }

    /**
     * Retorna Promise que resolve quando TODOS os frames estão carregados
     */
    getAllFramesPromise() {
        if (this.loaded) return Promise.resolve();
        if (this._allFramesPromise) return this._allFramesPromise;
        this._allFramesPromise = new Promise((resolve) => {
            this._resolveAllFrames = resolve;
        });
        return this._allFramesPromise;
    }

    /**
     * Sets up IntersectionObserver to start loading when approaching build section
     */
    setupLoadingObserver() {
        const config = FRAME_LOADING_CONFIG.build;

        // Observe the spacer element that triggers the build sequence
        const observerTarget = this.spacer || document.querySelector('.build-sequence__spacer');
        if (!observerTarget) {
            // Fallback: start loading immediately if no target found
            this.startBackgroundLoading();
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !this.hasStartedLoading) {
                        this.hasStartedLoading = true;
                        console.log(`👁️ BuildSequence: Approaching viewport, starting frame loading...`);
                        this.startBackgroundLoading();
                        observer.disconnect();
                    }
                });
            },
            {
                rootMargin: `${config.observerMargin}px 0px ${config.observerMargin}px 0px`,
                threshold: 0
            }
        );

        observer.observe(observerTarget);
        this.loadingObserver = observer;
    }

    /**
     * Preloads frames with prioritized loading:
     * 1. Load initial batch (for preloader)
     * 2. Set up observer to load remaining frames when approaching section
     */
    preloadImages() {
        const config = FRAME_LOADING_CONFIG.build;
        this.frameStep = config.frameStep;
        this.totalLoaded = 0;
        this.loadingQueue = [];
        this.isBackgroundLoading = false;
        this.hasStartedLoading = false;

        // Build list of frames to load (with step)
        this.framesToLoad = [];
        for (let i = 0; i < this.frameCount; i += this.frameStep) {
            this.framesToLoad.push(i);
        }
        this.effectiveFrameCount = this.framesToLoad.length;

        const initialFrameTarget = this._initialFrameTarget
            ? Math.min(this._initialFrameTarget, this.effectiveFrameCount)
            : Math.min(config.initialCount, this.effectiveFrameCount);

        // Create placeholder array for all images
        for (let i = 0; i < this.frameCount; i++) {
            this.images.push(null);
        }

        // Prepara a Promise dos frames iniciais se ainda não foi criada
        if (!this.initialFramesPromise) {
            this.getInitialFramesPromise(initialFrameTarget);
        }

        // Load initial frames immediately (for preloader)
        console.log(`🔄 BuildSequence: Loading initial ${initialFrameTarget} frames (step: ${this.frameStep})...`);
        for (let i = 0; i < initialFrameTarget; i++) {
            this.loadFrame(this.framesToLoad[i], true);
        }

        // Queue remaining frames for lazy loading
        for (let i = initialFrameTarget; i < this.effectiveFrameCount; i++) {
            this.loadingQueue.push(this.framesToLoad[i]);
        }

        // Set up observer to load remaining frames when approaching section
        this.setupLoadingObserver();
    }

    /**
     * Load a single frame
     * @param {number} index - Frame index to load
     * @param {boolean} isInitial - Whether this is part of initial batch
     */
    loadFrame(index, isInitial = false, retryCount = 0) {
        const MAX_RETRIES = 3;
        if (retryCount === 0 && this.images[index] !== null) return; // Already loaded or loading

        const img = new Image();
        this.images[index] = img; // Mark as loading

        img.onload = () => this.onFrameLoaded(index, isInitial);
        img.onerror = () => {
            if (retryCount < MAX_RETRIES) {
                const delay = Math.pow(2, retryCount) * 1000;
                setTimeout(() => {
                    this.images[index] = null;
                    this.loadFrame(index, isInitial, retryCount + 1);
                }, delay);
            } else {
                this.onFrameLoaded(index, isInitial);
            }
        };
        img.src = this.currentFrameSrc(index);
    }

    /**
     * Handle frame load completion
     */
    onFrameLoaded(index, isInitial) {
        this.totalLoaded++;
        const config = FRAME_LOADING_CONFIG.build;

        // Resolve Promise dos frames iniciais quando atingir o target
        if (!this.initialFramesLoaded && this.totalLoaded >= Math.min(config.initialCount, this.effectiveFrameCount)) {
            this.initialFramesLoaded = true;
            if (this._resolveInitialFrames) {
                this._resolveInitialFrames();
                console.log(`✅ BuildSequence: Initial ${config.initialCount} frames loaded (step: ${this.frameStep})`);
            }
        }

        // Quando todos carregam, configura animações
        if (this.totalLoaded >= this.effectiveFrameCount) {
            this.loaded = true;
            if (this._resolveAllFrames) this._resolveAllFrames();
            this.setupScrollAnimations();
            console.log(`✅ BuildSequence: All ${this.effectiveFrameCount} frames loaded (of ${this.frameCount} total)`);
        }
    }

    /**
     * Start background loading of remaining frames
     */
    startBackgroundLoading() {
        if (this.isBackgroundLoading || this.loadingQueue.length === 0) return;
        this.isBackgroundLoading = true;

        const config = FRAME_LOADING_CONFIG.build;
        console.log(`🔄 BuildSequence: Starting background loading of ${this.loadingQueue.length} remaining frames...`);

        const loadBatch = () => {
            if (this.loadingQueue.length === 0) {
                console.log('✅ BuildSequence: Background loading complete');
                return;
            }

            // Load a batch of frames
            const batch = this.loadingQueue.splice(0, config.batchSize);
            batch.forEach(index => this.loadFrame(index, false));

            // Schedule next batch
            if (this.loadingQueue.length > 0) {
                setTimeout(loadBatch, config.batchDelay);
            }
        };

        // Start loading batches
        loadBatch();
    }

    render() {
        let frameIndex = this.currentFrame;
        if (this.frameStep > 1) {
            frameIndex = Math.round(frameIndex / this.frameStep) * this.frameStep;
            frameIndex = Math.min(frameIndex, this.frameCount - 1);
        }

        const img = this.images[frameIndex];
        if (!img || !img.complete || !img.naturalWidth) return;

        if (IS_MOBILE) {
            this.renderer.updateFrame(img);
        } else {
            const canvasWidth = window.innerWidth;
            const canvasHeight = window.innerHeight;

            this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);

            const imageAspect = img.naturalWidth / img.naturalHeight;
            const canvasAspect = canvasWidth / canvasHeight;

            let drawWidth, drawHeight, drawX, drawY;

            if (imageAspect > canvasAspect) {
                drawHeight = canvasHeight;
                drawWidth = drawHeight * imageAspect;
                drawX = (canvasWidth - drawWidth) / 2;
                drawY = 0;
            } else {
                drawWidth = canvasWidth;
                drawHeight = drawWidth / imageAspect;
                drawX = 0;
                drawY = (canvasHeight - drawHeight) / 2;
            }

            this.context.drawImage(img, drawX, drawY, drawWidth, drawHeight);
        }
    }

    /**
     * Ativa o canvas da build-sequence (fade in)
     */
    activate() {
        if (this.isActive) return;
        this.isActive = true;

        this.canvas.classList.add('active');
        this.canvas.style.opacity = '';
        this.render();
        console.log('🏗️ Build sequence activated');
    }

    /**
     * Desativa o canvas da build-sequence (fade out)
     */
    deactivate() {
        if (!this.isActive) return;
        this.isActive = false;

        this.canvas.classList.remove('active');
        this.canvas.style.opacity = '';
        console.log('🏗️ Build sequence deactivated');
    }

    /**
     * Seta diretamente a opacidade dos elementos do planet info overlay (scroll-based)
     * @param {number} opacity - Valor de 0 a 1
     */
    setPlanetInfoOpacity(opacity) {
        if (!this.planetInfoOverlay) return;

        // Sinaliza controle ao PlanetManager
        if (this.planetManager && opacity < 1) {
            this.planetManager.infoControlledByBuild = true;
        }

        // Kill qualquer timeline pendente
        this.planetInfoTimeline?.kill();

        // No mobile, pin/contour/blob já sumiram na animação sequencial — não restaurar
        if (!IS_MOBILE) {
            gsap.set('.planet-sequence__blob', { opacity, y: (1 - opacity) * 10 });
            gsap.set('.planet-sequence__pin', { opacity, scale: opacity });
            gsap.set('.planet-sequence__contour', { opacity });
        }
        gsap.set(this.planetInfoOverlay, { opacity });

        if (opacity <= 0) {
            this.planetBottomCard?.classList.remove('active');
            this.planetInfoOverlay.classList.remove('active');
        } else {
            this.planetInfoOverlay.classList.add('active');
        }
    }

    /**
     * Faz fade in do info overlay do planet-sequence
     */
    fadeInPlanetInfo() {
        if (!this.planetInfoOverlay) return;

        // Devolve o controle ao PlanetManager
        if (this.planetManager) {
            this.planetManager.infoControlledByBuild = false;
            this.planetManager.infoShown = true; // Marca como mostrado para evitar re-animação
        }

        // Kill any running planet info timeline
        this.planetInfoTimeline?.kill();

        // Seta tudo visível instantaneamente (animação de entrada já ocorreu na 1ª vez via PlanetSequenceManager)
        this.planetInfoOverlay.classList.add('active');
        gsap.set(this.planetInfoOverlay, { opacity: 1 });
        // No mobile, pin/contour/blob ficam escondidos — só restaura no desktop
        if (!IS_MOBILE) {
            gsap.set('.planet-sequence__contour', { opacity: 1 });
            gsap.set('.planet-sequence__pin', { opacity: 1, scale: 1 });
            gsap.set('.planet-sequence__blob', { opacity: 1, y: 0 });
        }
        this.planetBottomCard?.classList.add('active');

        console.log('🏗️ Planet info overlay restored (instant)');
    }

    /**
     * Define opacidade do planet canvas (para transição de saída)
     * @param {number} opacity - Valor de opacidade (0 a 1)
     */
    setPlanetCanvasOpacity(opacity) {
        if (this.planetManager?.canvas) {
            this.planetManager.canvas.style.opacity = String(opacity);
        }
    }

    /**
     * Restaura opacidade do planet canvas (remove inline style)
     */
    restorePlanetCanvas() {
        if (this.planetManager?.canvas) {
            this.planetManager.canvas.style.opacity = '';
        }
    }

    /**
     * Anima as colunas do overlay final (one-shot, chamado quando o último frame é atingido)
     */
    revealColumns() {
        if (this.columnsRevealed || !this.columns.length) return;
        this.columnsRevealed = true;

        gsap.timeline()
            .to('.build-sequence__column--title', { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' })
            .to('.build-sequence__column--text', { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' }, '-=0.3')
            .to('.build-sequence__column--highlight', { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' }, '-=0.3');

        console.log('🏗️ Column reveals triggered');
    }

    /**
     * Reseta as colunas para o estado oculto (chamado ao scrollar de volta)
     */
    resetColumns() {
        if (!this.columnsRevealed || !this.columns.length) return;
        this.columnsRevealed = false;

        gsap.set('.build-sequence__column', { opacity: 0, y: 30 });

        console.log('🏗️ Columns reset to hidden');
    }

    setupScrollAnimations() {
        const scrollerConfig = window.lenis ? { scroller: document.documentElement } : {};

        let top, bottom;

        const updatePositions = () => {
            const spacerRect = this.spacer.getBoundingClientRect();
            top = spacerRect.top + window.scrollY;
            bottom = spacerRect.bottom - window.innerHeight + window.scrollY;

            console.log('🏗️ Build Sequence positions:', { top, bottom, scrollY: window.scrollY });
        };

        updatePositions();

        const FADE_START_FRAME = Math.round(this.frameCount * 0.05);
        const FADE_END_FRAME = Math.round(this.frameCount * 0.10);

        const onScroll = (scrollY) => {
            // Verifica se estamos na área da build-sequence
            const spacerRect = this.spacer.getBoundingClientRect();
            const spacerTop = spacerRect.top;
            const spacerBottom = spacerRect.bottom;

            // Verifica posição do lock spacer do planet (zona de transição)
            const lockSpacerRect = this.lockSpacer ? this.lockSpacer.getBoundingClientRect() : null;
            const isInLockZone = lockSpacerRect && lockSpacerRect.top < window.innerHeight && lockSpacerRect.bottom > 0;

            // Se o spacer está visível na viewport (build-sequence__spacer)
            const isInBuildSection = spacerTop < window.innerHeight && spacerBottom > 0;

            if (isInBuildSection) {
                // Ativa a build-sequence (sem esconder planet info ainda)
                if (!this.isActive) {
                    this.activate();
                }

                // Calcula progresso baseado no scroll
                const progress = Math.max(Math.min((scrollY - top) / (bottom - top), 1), 0);
                const targetFrame = Math.round(progress * (this.frameCount - 1));

                // Fade do planet info baseado no scroll (não time-based)
                if (targetFrame <= FADE_START_FRAME) {
                    this.setPlanetInfoOpacity(1);
                } else if (targetFrame >= FADE_END_FRAME) {
                    this.setPlanetInfoOpacity(0);
                } else {
                    const fadeProgress = (targetFrame - FADE_START_FRAME) / (FADE_END_FRAME - FADE_START_FRAME);
                    this.setPlanetInfoOpacity(1 - fadeProgress);
                }

                if (targetFrame !== this.currentFrame) {
                    this.currentFrame = targetFrame;
                    this.render();
                }

                if (!IS_MOBILE) {
                    if (progress >= 1) {
                        this.revealColumns();
                    } else {
                        this.resetColumns();
                    }
                }
            } else if (!isInBuildSection && !isInLockZone && spacerBottom <= 0) {
                if (!IS_MOBILE) {
                    // Desktop: desativa canvas normalmente
                    if (this.isActive) {
                        this.deactivate();
                    }
                    this.canvas.style.opacity = '';
                }
                // Mobile: canvas permanece mostrando último frame até o pin do overlay ativar
                this.setPlanetCanvasOpacity(0);
            } else if (isInLockZone) {
                // Estamos na zona de lock do planet - info overlay permanece visível
                if (this.isActive) {
                    this.deactivate();
                    this.fadeInPlanetInfo();
                }
                // Restaura planet canvas ao voltar para zona do planet
                this.restorePlanetCanvas();
            } else if (spacerTop >= window.innerHeight && !isInLockZone) {
                // Scrollou para cima - desativa build-sequence
                if (this.isActive) {
                    this.deactivate();
                    this.fadeInPlanetInfo();
                }
                // Restaura planet canvas
                this.restorePlanetCanvas();
            }
        };

        if (window.lenis) {
            window.lenis.on('scroll', ({ scroll }) => onScroll(scroll));
        } else {
            document.addEventListener('scroll', () => onScroll(window.scrollY));
        }

        window.addEventListener('resize', updatePositions);

        // Initial check at current scroll position
        onScroll(window.scrollY);

        // Mobile: pin + reveal columns
        if (IS_MOBILE) {
            this.setupMobileColumnReveal();
        }
    }

    /**
     * Mobile: pin do final overlay + reveal das colunas ao atingir o pin.
     */
    setupMobileColumnReveal() {
        if (!this.finalOverlay || !this.columns.length) return;

        gsap.set('.build-sequence__column', { opacity: 0, y: 30 });

        ScrollTrigger.create({
            trigger: this.finalOverlay,
            start: 'top top',
            end: '+=80vh',
            pin: true,
            pinSpacing: true,
            onEnter: () => {
                // Desativar canvas agora que o overlay está pinnado no topo
                this.deactivate();
                this.revealColumns();
            },
            onLeaveBack: () => {
                this.resetColumns();
            }
        });

        console.log('🏗️ Mobile column reveal ScrollTrigger (pin + once) initialized');
    }
}


/**
 * Gerencia a seção "Viver é ter espaço" com vídeo controlado por scroll
 * Usando técnica de IntersectionObserver + Fetch API para melhor performance
 */
class SpaceSectionManager {
    constructor() {
        this.section = document.querySelector('.space-section');
        this.video = document.querySelector('.space-section__video');
        this.spacer = document.querySelector('.space-section__spacer');
        this.title = document.querySelector('.space-section__title');
        this.text = document.querySelector('.space-section__text');
        this.highlight = document.querySelector('.space-section__highlight');
        this.decoration = document.querySelector('.space-section__decoration');

        if (!this.section || !this.video) {
            console.warn('SpaceSectionManager: Required elements not found');
            return;
        }

        // Cache de dados
        this.wrapperData = {
            video: this.video,
            top: 0,
            bottom: 0,
            duration: 0
        };

        // Estado
        this.isActive = false;
        this.videoLoaded = false;
        this.isPinned = false;
        this.textRevealsTriggered = false;

        // Inicializa
        this.setupIntersectionObserver();
        this.updateWrapperPositions();
        this.fetchVideo();
        this.setupScrollPin();
        this.setupScrollListener();
        this.setupResizeListener();
    }

    /**
     * Configura o pin da seção durante o scroll
     */
    setupScrollPin() {
        const scrollerConfig = window.lenis
            ? { scroller: document.documentElement }
            : {};

        ScrollTrigger.create({
            trigger: this.section,
            start: 'top top',
            end: () => `+=${this.spacer.offsetHeight}`,
            pin: true,
            pinSpacing: false,
            onEnter: () => {
                this.isPinned = true;
                console.log('📌 Space section pinned');
                // Inicia as animações de texto quando a seção é pinned
                this.triggerTextReveals();
            },
            onLeaveBack: () => {
                this.isPinned = false;
                console.log('📌 Space section unpinned (back)');
            },
            ...scrollerConfig
        });

        console.log('✅ Space section pin initialized');
    }

    /**
     * Força o carregamento completo do vídeo usando Fetch API
     * Isso garante que todo o vídeo está disponível para seeking suave
     */
    fetchVideo() {
        // Already preloaded as blob by the preloader
        if (this.video._blobLoaded) {
            if (this.video.readyState >= 1) {
                this.wrapperData.duration = this.video.duration;
                this.videoLoaded = true;
                console.log('✅ Space section video already preloaded, duration:', this.video.duration);
            } else {
                this.video.addEventListener('loadedmetadata', () => {
                    this.wrapperData.duration = this.video.duration;
                    this.videoLoaded = true;
                    console.log('✅ Space section video preloaded (metadata ready), duration:', this.video.duration);
                }, { once: true });
            }
            return;
        }

        const src = this.video.getAttribute('src');

        fetch(src)
            .then((response) => response.blob())
            .then((response) => {
                // Cria URL de dados contendo o vídeo completo
                const objectURL = URL.createObjectURL(response);

                // Atribui ao elemento de vídeo
                this.video.setAttribute('src', objectURL);

                // Aguarda metadados após carregar o blob
                this.video.addEventListener('loadedmetadata', () => {
                    this.wrapperData.duration = this.video.duration;
                    this.videoLoaded = true;
                    console.log('✅ Space section video fetched and loaded, duration:', this.video.duration);
                }, { once: true });
            })
            .catch((error) => {
                console.error('❌ Error fetching video:', error);
                // Fallback: usa o vídeo original
                this.video.addEventListener('loadedmetadata', () => {
                    this.wrapperData.duration = this.video.duration;
                    this.videoLoaded = true;
                }, { once: true });
            });
    }

    /**
     * Cria IntersectionObserver para detectar quando a seção está na viewport
     */
    setupIntersectionObserver() {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    // Está completamente visível na viewport?
                    const isWithinViewport = entry.intersectionRatio >= 0.1;

                    // Adiciona/remove classe 'in-view'
                    entry.target.classList.toggle('in-view', isWithinViewport);

                    if (isWithinViewport) {
                        this.isActive = true;
                    } else {
                        this.isActive = false;
                    }
                });
            },
            { threshold: [0, 0.1, 0.5, 1] }
        );

        observer.observe(this.section);
    }

    /**
     * Atualiza as posições do wrapper (top/bottom) para cálculo do progresso
     */
    updateWrapperPositions() {
        const sectionRect = this.section.getBoundingClientRect();
        const spacerRect = this.spacer.getBoundingClientRect();

        this.wrapperData.top = sectionRect.top + window.scrollY;
        this.wrapperData.bottom = spacerRect.bottom - window.innerHeight + window.scrollY;

        console.log('📍 Space section positions updated:', {
            top: this.wrapperData.top,
            bottom: this.wrapperData.bottom
        });
    }

    /**
     * Configura o listener de scroll
     */
    setupScrollListener() {
        const handleScroll = (scrollY) => {
            // Só processa se a seção estiver ativa e o vídeo carregado
            if (!this.isActive || !this.videoLoaded) return;

            const { top, bottom, duration } = this.wrapperData;

            // Calcula progresso (0 a 0.998 para evitar erros de arredondamento)
            const progress = Math.max(
                Math.min((scrollY - top) / (bottom - top), 0.998),
                0
            );

            // Calcula o tempo de seek baseado no progresso
            const seekTime = progress * duration;

            // Pula para o tempo correto
            this.video.currentTime = seekTime;
        };

        if (window.lenis) {
            window.lenis.on('scroll', ({ scroll }) => handleScroll(scroll));
        } else {
            document.addEventListener('scroll', () => handleScroll(window.scrollY));
        }

        // Check inicial
        handleScroll(window.scrollY);
    }

    /**
     * Configura o listener de resize
     */
    setupResizeListener() {
        window.addEventListener('resize', () => {
            this.updateWrapperPositions();
        });
    }

    /**
     * Dispara as animações de reveal do texto (chamado quando a seção é pinned)
     */
    triggerTextReveals() {
        if (this.textRevealsTriggered) return;
        this.textRevealsTriggered = true;

        // Timeline de animações sequenciais
        this.textRevealTimeline = gsap.timeline();

        // Decoração SVG - slide da direita
        if (this.decoration) {
            this.textRevealTimeline.to(this.decoration, {
                opacity: 1,
                x: 0,
                duration: 0.8,
                ease: 'power2.out'
            }, 0);
        }

        // Título - fade + slide up
        if (this.title) {
            this.textRevealTimeline.to(this.title, {
                opacity: 1,
                y: 0,
                duration: 0.6,
                ease: 'power2.out'
            }, 0.2);
        }

        // Texto principal - fade + slide up
        if (this.text) {
            this.textRevealTimeline.to(this.text, {
                opacity: 1,
                y: 0,
                duration: 0.6,
                ease: 'power2.out'
            }, 0.4);
        }

        // Highlight - fade + slide up
        if (this.highlight) {
            this.textRevealTimeline.to(this.highlight, {
                opacity: 1,
                y: 0,
                duration: 0.6,
                ease: 'power2.out'
            }, 0.6);
        }

        console.log('✅ Space section text reveals triggered');
    }

    /**
     * Reverte as animações de reveal do texto (chamado quando sai do pin para cima)
     */
    reverseTextReveals() {
        if (!this.textRevealsTriggered) return;
        this.textRevealsTriggered = false;

        // Timeline de animações para esconder
        gsap.timeline()
            .to(this.highlight, { opacity: 0, y: 30, duration: 0.3 }, 0)
            .to(this.text, { opacity: 0, y: 30, duration: 0.3 }, 0.1)
            .to(this.title, { opacity: 0, y: 30, duration: 0.3 }, 0.2)
            .to(this.decoration, { opacity: 0, x: 20, duration: 0.3 }, 0.2);

        console.log('✅ Space section text reveals reversed');
    }
}


/**
 * Gerencia a seção "Viver é escolher o que funciona" com ScrollTrigger pin + scrub
 *
 * Fluxo da animação:
 * 1. Seção trava (pin) quando chega no topo
 * 2. Elementos aparecem em sequência (reveal com scrub)
 * 3. Pausa para leitura
 * 4. Todos os elementos somem (fade out)
 * 5. Tela fica preta (#282828)
 * 6. Seção desgrava (unpin)
 */
class FuncionaSectionManager {
    constructor() {
        this.section = document.querySelector('.funciona-section');
        this.decoration = document.querySelector('.funciona-section__decoration');
        this.title = document.querySelector('.funciona-section__title');
        this.description = document.querySelector('.funciona-section__description');
        this.cards = document.querySelectorAll('.funciona-section__card');
        this.btn = document.querySelector('.funciona-section__btn');
        this.highlight = document.querySelector('.funciona-section__highlight');

        if (!this.section) {
            console.warn('FuncionaSectionManager: Required elements not found');
            return;
        }

        this.setupScrollAnimation();
    }

    setupScrollAnimation() {
        if (this.decoration) gsap.set(this.decoration, { autoAlpha: 0, x: 20 });
        const yItems = [this.title, this.description, this.btn, this.highlight].filter(Boolean);
        if (yItems.length) gsap.set(yItems, { autoAlpha: 0, y: 30 });
        if (this.cards.length) gsap.set(this.cards, { autoAlpha: 0, y: 22 });

        const tl = gsap.timeline({
            paused: true,
            scrollTrigger: {
                trigger: this.section,
                start: 'top 55%',
                toggleActions: 'play none none reset',
            },
        });

        if (this.decoration) {
            tl.fromTo(this.decoration,
                { autoAlpha: 0, x: 20 },
                { autoAlpha: 1, x: 0, duration: 1.8, ease: 'power3.out' });
        }
        if (this.title) {
            tl.fromTo(this.title,
                { autoAlpha: 0, y: 30 },
                { autoAlpha: 1, y: 0, duration: 1.6, ease: 'power3.out' }, '-=1.2');
        }
        if (this.description) {
            tl.fromTo(this.description,
                { autoAlpha: 0, y: 22 },
                { autoAlpha: 1, y: 0, duration: 1.4, ease: 'power3.out' }, '-=1.0');
        }
        if (this.cards.length) {
            tl.fromTo(this.cards,
                { autoAlpha: 0, y: 22 },
                { autoAlpha: 1, y: 0, duration: 1.3, ease: 'power3.out', stagger: 0.55 }, '-=0.8');
        }
        if (this.btn) {
            tl.fromTo(this.btn,
                { autoAlpha: 0, y: 22 },
                { autoAlpha: 1, y: 0, duration: 1.2, ease: 'power3.out' }, '-=0.5');
        }
        if (this.highlight) {
            tl.fromTo(this.highlight,
                { autoAlpha: 0, y: 22 },
                { autoAlpha: 1, y: 0, duration: 1.2, ease: 'power3.out' }, '-=0.9');
        }

    }
}

class TowersSectionManager {
    constructor() {
        this.section = document.querySelector('.towers-section');
        this.mapContainer = document.querySelector('.towers-map-container');
        this.navItems = document.querySelectorAll('.towers-nav__item');
        this.produtosHome = document.querySelector('.produtos-home.produtos');

        if (!this.section) {
            console.warn('TowersSectionManager: Required elements not found');
            return;
        }

        this.setupScrollAnimation();
        this.setupProdutosHome();
    }

    setupScrollAnimation() {
        if (this.mapContainer) gsap.set(this.mapContainer, { autoAlpha: 0 });
        if (this.navItems.length) gsap.set(this.navItems, { autoAlpha: 0, y: 20 });

        const tl = gsap.timeline({
            paused: true,
            scrollTrigger: {
                trigger: this.section,
                start: 'top 55%',
                toggleActions: 'play none none reset',
            },
        });

        if (this.mapContainer) {
            tl.fromTo(this.mapContainer,
                { autoAlpha: 0 },
                { autoAlpha: 1, duration: 1.0, ease: 'power2.out' });
        }

        if (this.navItems.length) {
            tl.fromTo(this.navItems,
                { autoAlpha: 0, y: 20 },
                { autoAlpha: 1, y: 0, duration: 0.6, ease: 'power3.out', stagger: 0.08 },
                '-=0.4');
        }
    }

    /**
     * Mapa interativo de produtos no estilo da página de produto-detail-desk,
     * agora dentro da home (desktop). Sincroniza tabs ↔ áreas do SVG ↔ background.
     */
    setupProdutosHome() {
        const prod = this.produtosHome;
        if (!prod) return;

        const tabs = prod.querySelectorAll('.produtos__tab');
        const bgs = prod.querySelectorAll('.produtos__bg');
        const para = prod.querySelector('#prod-para-home');
        const desc = prod.querySelector('#prod-desc-home');
        const link = prod.querySelector('#prod-link-home');
        const mapAreas = prod.querySelectorAll('.map-area-predios');

        const data = {};
        tabs.forEach(tab => {
            const key = tab.dataset.prod;
            data[key] = {
                para: tab.dataset.para || '',
                title: tab.dataset.title || '',
                desc: tab.dataset.desc || '',
                link: tab.dataset.href || '#'
            };
        });

        const hasGsap = typeof gsap !== 'undefined';
        const elements = [para, desc, link].filter(Boolean);
        if (hasGsap && elements.length) {
            gsap.set(elements, { autoAlpha: 0, y: 30 });
        }

        let pinned = 'default';

        const update = (key) => {
            tabs.forEach(t => t.classList.toggle('is-active', t.dataset.prod === key && key !== 'default'));
            bgs.forEach(bg => {
                bg.classList.remove('is-hover');
                bg.classList.toggle('is-active', bg.dataset.prod === key);
            });

            if (!hasGsap) {
                if (data[key]) {
                    if (para) para.innerHTML = data[key].para;
                    if (desc) desc.innerHTML = data[key].desc;
                    if (link) {
                        link.href = data[key].link;
                        link.classList.remove('produtos__cta--hidden');
                    }
                } else {
                    if (para) para.innerHTML = '';
                    if (desc) desc.innerHTML = '';
                }
                return;
            }

            gsap.killTweensOf(elements);
            if (data[key]) {
                gsap.to(elements, {
                    autoAlpha: 0, y: -20, duration: 0.3, ease: 'power2.in',
                    onComplete: () => {
                        if (para) para.innerHTML = data[key].para;
                        if (desc) desc.innerHTML = data[key].desc;
                        if (link) link.href = data[key].link;
                        gsap.set(elements, { y: 30, autoAlpha: 0 });
                        if (key === 'rooftop' && link) {
                            gsap.set(link, { autoAlpha: 0, pointerEvents: 'none' });
                        } else if (link) {
                            gsap.set(link, { pointerEvents: 'auto' });
                            link.classList.remove('produtos__cta--hidden');
                        }
                        gsap.to(elements, {
                            autoAlpha: (i, el) => (el === link && key === 'rooftop') ? 0 : 1,
                            y: 0, duration: 0.8, ease: 'expo.out', stagger: 0.1, clearProps: 'transform'
                        });
                    }
                });
            } else {
                gsap.to(elements, {
                    autoAlpha: 0, y: -20, duration: 0.4, ease: 'power2.in',
                    onComplete: () => {
                        if (para) para.innerHTML = '';
                        if (desc) desc.innerHTML = '';
                    }
                });
            }
        };

        tabs.forEach(tab => {
            tab.addEventListener('click', () => {
                const key = tab.dataset.prod;
                pinned = (key === pinned) ? 'default' : key;
                update(pinned);
            });
        });

        const setHoverPreview = (key) => {
            bgs.forEach(bg => {
                const isPinned = bg.classList.contains('is-active');
                bg.classList.toggle('is-hover', !isPinned && bg.dataset.prod === key);
            });
        };

        const clearHoverPreview = () => {
            bgs.forEach(bg => bg.classList.remove('is-hover'));
        };

        mapAreas.forEach(area => {
            area.addEventListener('mouseenter', () => setHoverPreview(area.dataset.id));
            area.addEventListener('mouseleave', clearHoverPreview);
            area.addEventListener('click', (e) => {
                e.preventDefault();
                const key = area.dataset.id;
                pinned = (key === pinned) ? 'default' : key;
                update(pinned);
                clearHoverPreview();
            });
        });
    }
}


/**
 * Gerencia a seção "Silêncio" unificada com efeito de zoom e reveal
 *
 * Fluxo da animação:
 * 1. Seção trava (pin) quando chega no topo
 * 2. Título "Viver também é silêncio." aparece
 * 3. "Viver também é" e "." fazem fade out
 * 4. Palavra "silêncio" escala até cobrir toda a tela de branco
 * 5. Fade out da palavra gigante revela o vídeo
 * 6. Textos aparecem sobre o vídeo
 * 7. Seção desgrava (unpin)
 */
class SilencioSectionManager {
    constructor() {
        this.section = document.querySelector('.silencio-section');

        if (!this.section) {
            console.warn('⚠️ Silencio section not found');
            return;
        }

        // Elementos do intro (título)
        this.intro = this.section.querySelector('.silencio-section__intro');
        this.title = this.section.querySelector('.silencio-section__title');
        this.prefix = this.section.querySelector('.silencio-title__prefix');
        this.word = this.section.querySelector('.silencio-title__word');
        this.suffix = this.section.querySelector('.silencio-title__suffix');

        // Elementos do reveal (textos sobre vídeo)
        this.revealContent = this.section.querySelector('.silencio-section__reveal-content');
        this.text = this.section.querySelector('.silencio-section__text');
        this.subtitle = this.section.querySelector('.silencio-section__subtitle');

        this.init();
    }

    init() {
        // URL do SVG (mesma usada no preload global)
        this.svgUrl = (window.APP_CONFIG ? window.APP_CONFIG.assetPath : '/') + 'site/img/home/silencio.svg';

        // Aguarda SVG estar pronto antes de criar o ScrollTrigger
        // Isso elimina a race condition onde onStart disparava antes do SVG carregar
        const svgPromise = window._silencioSvgReady || Promise.resolve(true);
        svgPromise.then((loaded) => {
            this.svgReady = loaded;
            this.svgBlobUrl = window._silencioSvgBlobUrl || this.svgUrl;
            this.setupScrollAnimation();
            console.log('✅ Silencio section manager initialized');
        });
    }

    setupScrollAnimation() {
        if (!this.title || !this.word || !this.intro) return;

        // Garante que o SVG escala do centro
        gsap.set(this.word, { transformOrigin: 'center center' });

        // Calcula largura inicial da palavra para o mask-size inicial
        const wordRect = this.word.getBoundingClientRect();
        const introRect = this.intro.getBoundingClientRect();
        const initialMaskWidth = wordRect.width;

        // Centro da palavra relativo ao container intro (para posicionar a mask)
        const wordCenterX = wordRect.left - introRect.left + wordRect.width / 2;
        const wordCenterY = wordRect.top - introRect.top + wordRect.height / 2;
        const svgAspectRatio = 549 / 138; // silencio.svg viewBox ratio

        // URL do SVG silêncio (blob URL se disponível, fallback para URL original)
        const svgUrl = this.svgBlobUrl || this.svgUrl;

        // Pre-ativar mask com tamanho 0 (invisível — overlay totalmente visível)
        // Com mask-size: 0px + mask-composite: exclude, cutout de 0px = overlay 100% visível
        this.intro.style.setProperty('--silencio-mask-url', `url('${svgUrl}')`);
        this.intro.style.setProperty('--mask-size', '0px');
        this.intro.style.setProperty('--mask-pos-x', `${wordCenterX}px`);
        this.intro.style.setProperty('--mask-pos-y', `${wordCenterY}px`);
        this.intro.classList.add('has-mask');

        // Proxy para animação numérica do mask-size via GSAP (começa em 0)
        const maskProxy = { size: 0 };

        // Timeline principal controlada pelo scroll com pin
        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: this.section,
                start: 'top top',
                end: '+=600vh', // Mais scroll = transição mais lenta e suave
                pin: true,
                scrub: 1, // Interpolação suave (1s de lag)
                anticipatePin: 1,
                // markers: true // Descomente para debug
            }
        });

        // ===== PARTE 1: Título e Mask Reveal =====

        // Fase 1 (0% - 5%): Título completo aparece
        tl.to(this.title, {
            opacity: 1,
            duration: 0.05,
            ease: 'power1.out'
        });

        // Fase 2 (12% - 22%): Pausa para leitura
        tl.to({}, { duration: 0.10 });

        // Fase 3 (22% - 30%): Prefix e suffix fazem fade out
        tl.to([this.prefix, this.suffix], {
            opacity: 0,
            duration: 0.08,
            ease: 'power1.in'
        });

        // Fase 4 (30% - 32%): Palavra some, mask cutout aparece no tamanho da palavra
        tl.to(this.word, {
            opacity: 0,
            duration: 0.02,
            ease: 'none',
            onReverseComplete: () => {
                // Ao reverter, volta mask para tamanho 0 (overlay totalmente visível)
                maskProxy.size = 0;
                this.intro.style.setProperty('--mask-size', '0px');
            }
        });
        // Simultaneamente, mask cresce de 0 ao tamanho da palavra
        tl.to(maskProxy, {
            size: initialMaskWidth,
            duration: 0.02,
            ease: 'none',
            onUpdate: () => {
                const s = maskProxy.size;
                const h = s / svgAspectRatio;
                this.intro.style.setProperty('--mask-size', `${s}px`);
                this.intro.style.setProperty('--mask-pos-x', `${wordCenterX - s / 2}px`);
                this.intro.style.setProperty('--mask-pos-y', `${wordCenterY - h / 2}px`);
            }
        }, '<'); // '<' = mesmo tempo que a animação anterior

        // Fase 5 (32% - 72%): Mask cresce - cutout expande revelando o vídeo
        tl.to(maskProxy, {
            size: 8000,
            duration: 0.40,
            ease: 'power3.in', // Começa lento, acelera = sensação de "mergulho"
            onUpdate: () => {
                const s = maskProxy.size;
                const h = s / svgAspectRatio;
                this.intro.style.setProperty('--mask-size', `${s}px`);
                this.intro.style.setProperty('--mask-pos-x', `${wordCenterX - s / 2}px`);
                this.intro.style.setProperty('--mask-pos-y', `${wordCenterY - h / 2}px`);
            }
        });

        // Fase 6 (72% - 78%): Fade out do intro overlay (cleanup de segurança)
        tl.to(this.intro, {
            opacity: 0,
            duration: 0.06,
            ease: 'power1.in'
        });

        // ===== PARTE 2: Reveal dos textos sobre vídeo =====

        // Fase 7 (78% - 82%): Reveal content aparece
        tl.to(this.revealContent, {
            opacity: 1,
            pointerEvents: 'auto',
            duration: 0.04
        });

        // Fase 8 (82% - 90%): Texto aparece
        if (this.text) {
            tl.to(this.text, {
                opacity: 1,
                y: 0,
                duration: 0.08,
                ease: 'power2.out'
            });
        }

        // Fase 9 (90% - 96%): Subtitle aparece
        if (this.subtitle) {
            tl.to(this.subtitle, {
                opacity: 1,
                y: 0,
                duration: 0.06,
                ease: 'power2.out'
            });
        }

        // Fase 10 (96% - 100%): Estabilização antes de desgravar
        tl.to({}, { duration: 0.04 });
    }
}


/**
 * Gerencia a seção "Viver em arquitetura que atravessa gerações" com vídeo controlado por scroll
 * Baseado no SpaceSectionManager, mas com layout invertido (texto esquerda, vídeo direita)
 */
class ArquiteturaSectionManager {
    constructor() {
        this.section = document.querySelector('.arquitetura-section');
        this.video = document.querySelector('.arquitetura-section__video');
        this.spacer = document.querySelector('.arquitetura-section__spacer');
        this.title = document.querySelector('.arquitetura-section__title');
        this.text = document.querySelector('.arquitetura-section__text');
        this.logos = document.querySelector('.arquitetura-section__logos');

        if (!this.section || !this.video) {
            console.warn('ArquiteturaSectionManager: Required elements not found');
            return;
        }

        // Mobile: sem pin/scrub — vídeo em autoplay loop
        if (IS_MOBILE) {
            this.video.setAttribute('autoplay', '');
            this.video.setAttribute('loop', '');
            safeAutoplay(this.video);
            this.setupIntersectionObserver();
            return;
        }

        // Cache de dados
        this.wrapperData = {
            video: this.video,
            top: 0,
            bottom: 0,
            duration: 0
        };

        // Estado
        this.isActive = false;
        this.videoLoaded = false;
        this.isPinned = false;
        this.textRevealsTriggered = false;

        // Inicializa
        this.setupIntersectionObserver();
        this.updateWrapperPositions();
        this.fetchVideo();
        this.setupScrollPin();
        this.setupScrollListener();
        this.setupResizeListener();
    }

    /**
     * Configura o pin da seção durante o scroll
     */
    setupScrollPin() {
        const scrollerConfig = window.lenis
            ? { scroller: document.documentElement }
            : {};

        ScrollTrigger.create({
            trigger: this.section,
            start: 'top top',
            end: () => `+=${this.spacer.offsetHeight}`,
            pin: true,
            pinSpacing: false,
            onEnter: () => {
                this.isPinned = true;
                this.triggerTextReveals();
            },
            onLeaveBack: () => {
                this.isPinned = false;
            },
            ...scrollerConfig
        });
    }

    /**
     * Força o carregamento completo do vídeo usando Fetch API
     */
    fetchVideo() {
        // Already preloaded as blob by the preloader
        if (this.video._blobLoaded) {
            if (this.video.readyState >= 1) {
                this.wrapperData.duration = this.video.duration;
                this.videoLoaded = true;
                console.log('✅ Arquitetura video already preloaded, duration:', this.video.duration);
            } else {
                this.video.addEventListener('loadedmetadata', () => {
                    this.wrapperData.duration = this.video.duration;
                    this.videoLoaded = true;
                    console.log('✅ Arquitetura video preloaded (metadata ready), duration:', this.video.duration);
                }, { once: true });
            }
            return;
        }

        const src = this.video.getAttribute('src');

        fetch(src)
            .then((response) => response.blob())
            .then((response) => {
                const objectURL = URL.createObjectURL(response);
                this.video.setAttribute('src', objectURL);

                this.video.addEventListener('loadedmetadata', () => {
                    this.wrapperData.duration = this.video.duration;
                    this.videoLoaded = true;
                }, { once: true });
            })
            .catch((error) => {
                console.error('Error fetching arquitetura video:', error);
                this.video.addEventListener('loadedmetadata', () => {
                    this.wrapperData.duration = this.video.duration;
                    this.videoLoaded = true;
                }, { once: true });
            });
    }

    /**
     * Cria IntersectionObserver para detectar quando a seção está na viewport
     */
    setupIntersectionObserver() {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const isWithinViewport = entry.intersectionRatio >= 0.1;
                    entry.target.classList.toggle('in-view', isWithinViewport);

                    if (isWithinViewport) {
                        this.isActive = true;
                    } else {
                        this.isActive = false;
                    }
                });
            },
            { threshold: [0, 0.1, 0.5, 1] }
        );

        observer.observe(this.section);
    }

    /**
     * Atualiza as posições do wrapper (top/bottom) para cálculo do progresso
     */
    updateWrapperPositions() {
        const sectionRect = this.section.getBoundingClientRect();
        const spacerRect = this.spacer.getBoundingClientRect();

        this.wrapperData.top = sectionRect.top + window.scrollY;
        this.wrapperData.bottom = spacerRect.bottom - window.innerHeight + window.scrollY;
    }

    /**
     * Configura o listener de scroll
     */
    setupScrollListener() {
        const handleScroll = (scrollY) => {
            if (!this.isPinned || !this.videoLoaded) return;

            const { top, bottom, duration } = this.wrapperData;

            const progress = Math.max(
                Math.min((scrollY - top) / (bottom - top), 0.998),
                0
            );

            const seekTime = progress * duration;
            this.video.currentTime = seekTime;
        };

        if (window.lenis) {
            window.lenis.on('scroll', ({ scroll }) => handleScroll(scroll));
        } else {
            document.addEventListener('scroll', () => handleScroll(window.scrollY));
        }
    }

    /**
     * Configura o listener de resize
     */
    setupResizeListener() {
        window.addEventListener('resize', () => {
            this.updateWrapperPositions();
        });
    }

    /**
     * Dispara as animações de reveal do texto
     */
    triggerTextReveals() {
        if (this.textRevealsTriggered) return;
        this.textRevealsTriggered = true;

        this.textRevealTimeline = gsap.timeline();

        // Título - fade + slide up
        if (this.title) {
            this.textRevealTimeline.to(this.title, {
                opacity: 1,
                y: 0,
                duration: 0.6,
                ease: 'power2.out'
            }, 0.2);
        }

        // Texto principal - fade + slide up
        if (this.text) {
            this.textRevealTimeline.to(this.text, {
                opacity: 1,
                y: 0,
                duration: 0.6,
                ease: 'power2.out'
            }, 0.4);
        }

        // Logos - fade + slide up
        if (this.logos) {
            this.textRevealTimeline.to(this.logos, {
                opacity: 1,
                y: 0,
                duration: 0.6,
                ease: 'power2.out'
            }, 0.6);
        }
    }

    /**
     * Reverte as animações de reveal do texto
     */
    reverseTextReveals() {
        if (!this.textRevealsTriggered) return;
        this.textRevealsTriggered = false;

        gsap.timeline()
            .to(this.logos, { opacity: 0, y: 30, duration: 0.3 }, 0)
            .to(this.text, { opacity: 0, y: 30, duration: 0.3 }, 0.1)
            .to(this.title, { opacity: 0, y: 30, duration: 0.3 }, 0.2);
    }
}


/**
 * Gerencia seções com vídeo em loop e text reveal único.
 * Fluxo:
 * 1. Quando a seção entra na viewport, título e texto fazem reveal sequencial (fade + slide up)
 * 2. Texto permanece visível para sempre (não faz fade out, não re-anima)
 */
class VideoRevealSectionManager {
    constructor(sectionClass) {
        this.section = document.querySelector(`.${sectionClass}`);

        if (!this.section) {
            console.warn(`${sectionClass}: Required elements not found`);
            return;
        }

        this.title = this.section.querySelector(`.${sectionClass}__title`);
        this.text = this.section.querySelector(`.${sectionClass}__text`);
        this.sectionClass = sectionClass;
        this.revealed = false;

        this.setupScrollAnimation();
    }

    setupScrollAnimation() {
        const scrollerConfig = window.lenis ? { scroller: document.documentElement } : {};

        // Timeline de reveal (pausada, será disparada uma única vez)
        this.revealTimeline = gsap.timeline({ paused: true });

        // Título - fade + slide up
        if (this.title) {
            this.revealTimeline.to(this.title, {
                opacity: 1,
                y: 0,
                duration: 0.8,
                ease: 'power2.out'
            }, 0);
        }

        // Texto - fade + slide up com delay
        if (this.text) {
            this.revealTimeline.to(this.text, {
                opacity: 1,
                y: 0,
                duration: 0.8,
                ease: 'power2.out'
            }, 0.3);
        }

        // Pin a seção no topo da viewport por 150vh — o usuário precisa scrollar
        // esse extra para passar para a próxima seção (efeito de "segurada")
        ScrollTrigger.create({
            trigger: this.section,
            start: 'top top',
            end: '+=150vh',
            pin: true,
            pinSpacing: true,
            onEnter: () => {
                if (!this.revealed) {
                    this.revealed = true;
                    this.revealTimeline.play();
                    console.log(`✅ ${this.sectionClass} text reveal triggered`);
                }
            },
            ...scrollerConfig
        });
    }
}


/**
 * Lazy Video Loader - Loads videos only when they approach the viewport
 * Prevents unnecessary bandwidth usage for below-fold videos
 */
class LazyVideoLoader {
    constructor() {
        this.loadedVideos = new Set();
        this.observer = null;
        this.rootMargin = '500px 0px 500px 0px'; // Load when 500px from viewport
    }

    /**
     * Initialize lazy loading for all videos with data-src attribute
     */
    init() {
        const lazyVideos = document.querySelectorAll('video[data-src]');

        if (lazyVideos.length === 0) {
            console.log('📹 LazyVideoLoader: No lazy videos found');
            return;
        }

        this.observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this.loadVideo(entry.target);
                    }
                });
            },
            {
                rootMargin: this.rootMargin,
                threshold: 0
            }
        );

        lazyVideos.forEach((video) => {
            this.observer.observe(video);
        });

        console.log(`📹 LazyVideoLoader: Observing ${lazyVideos.length} videos`);
    }

    /**
     * Load a video by setting its src from data-src
     */
    loadVideo(video) {
        if (this.loadedVideos.has(video)) return;
        this.loadedVideos.add(video);

        const dataSrc = IS_MOBILE
            ? (video.dataset.srcMobile || video.dataset.src)
            : video.dataset.src;
        if (!dataSrc) return;

        video.src = dataSrc;
        video.removeAttribute('data-src');
        if (video.dataset.srcMobile) video.removeAttribute('data-src-mobile');
        video.load();

        // If autoplay is set, play the video (com fallback para Low Power Mode)
        if (video.hasAttribute('autoplay')) {
            safeAutoplay(video);
        }

        console.log(`📹 LazyVideoLoader: Loaded video`, video.className);

        // Stop observing this video
        if (this.observer) {
            this.observer.unobserve(video);
        }
    }
}


/**
 * Lazy Section Loader - Initializes sections only when they approach the viewport
 * Critical sections (intro, planet, build) are initialized immediately
 * Other sections are initialized when 300px from viewport
 */
class LazySectionLoader {
    constructor() {
        this.initializedSections = new Set();
        this.sectionConfigs = [];
        this.observer = null;
        this.rootMargin = '300px 0px 300px 0px'; // Load when 300px from viewport
    }

    /**
     * Register a section for lazy initialization
     * @param {string} selector - CSS selector for the section trigger element
     * @param {Function} initFn - Function to call to initialize the section
     * @param {string} name - Name for logging
     */
    register(selector, initFn, name) {
        this.sectionConfigs.push({ selector, initFn, name });
    }

    /**
     * Start observing registered sections
     */
    start() {
        if (this.sectionConfigs.length === 0) return;

        this.observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const selector = entry.target.dataset.lazySection;
                        if (selector && !this.initializedSections.has(selector)) {
                            this.initializeSection(selector);
                        }
                    }
                });
            },
            {
                rootMargin: this.rootMargin,
                threshold: 0
            }
        );

        // Observe each registered section
        this.sectionConfigs.forEach(({ selector, name }) => {
            const element = document.querySelector(selector);
            if (element) {
                element.dataset.lazySection = selector;
                this.observer.observe(element);
                console.log(`👁️ LazySectionLoader: Observing ${name}`);
            } else {
                console.warn(`⚠️ LazySectionLoader: Element not found for ${name} (${selector})`);
            }
        });
    }

    /**
     * Initialize a section by selector
     */
    initializeSection(selector) {
        const config = this.sectionConfigs.find(c => c.selector === selector);
        if (!config || this.initializedSections.has(selector)) return;

        this.initializedSections.add(selector);

        try {
            config.initFn();
            console.log(`✅ LazySectionLoader: Initialized ${config.name}`);
        } catch (error) {
            console.error(`❌ LazySectionLoader: Error initializing ${config.name}:`, error);
        }

        // Stop observing this element
        const element = document.querySelector(selector);
        if (element && this.observer) {
            this.observer.unobserve(element);
        }
    }

    /**
     * Force initialize all remaining sections (cleanup)
     */
    initializeAll() {
        this.sectionConfigs.forEach(({ selector }) => {
            if (!this.initializedSections.has(selector)) {
                this.initializeSection(selector);
            }
        });
    }
}


/*---------------------/ 2 - Functions /-------------------*/

/**
 * Inicializa e esconde o preloader quando TODOS os recursos estiverem prontos.
 * Bloqueia scroll durante carregamento.
 * Aguarda: vídeo intro, TODOS os frames (planet + build), vídeos scrub,
 *          vídeos lazy, SVG silencio, fontes.
 */
async function initPreloader(introManager, planetManager, buildManager) {
    const loader = document.querySelector('.home-loader');
    if (!loader) return;

    // === SCROLL LOCK ===
    document.documentElement.style.overflow = 'hidden';
    if (window.lenis) window.lenis.stop();

    const MIN_PRELOADER_TIME = 2500;  // 2.5s - tempo mínimo para UX
    const MAX_PRELOADER_TIME = 60000; // 60s - timeout máximo
    const preloaderStartTime = Date.now();

    // Start loading ALL remaining frames immediately (don't wait for preloader to hide)
    if (planetManager && typeof planetManager.startBackgroundLoading === 'function') {
        planetManager.startBackgroundLoading();
    }
    if (buildManager && typeof buildManager.startBackgroundLoading === 'function') {
        buildManager.startBackgroundLoading();
    }

    // --- Promise helpers ---

    const waitForIntroVideo = () => {
        return new Promise((resolve) => {
            const introVideo = document.getElementById('introVideo');
            if (!introVideo) { resolve(); return; }
            if (introVideo.readyState >= 1) { resolve(); return; }
            const onLoaded = () => resolve();
            introVideo.addEventListener('loadedmetadata', onLoaded, { once: true });
            introVideo.addEventListener('canplay', onLoaded, { once: true });
        });
    };

    const preloadScrubVideo = (selector) => {
        return new Promise((resolve) => {
            const video = document.querySelector(selector);
            if (!video) { resolve(); return; }
            const src = video.getAttribute('src');
            if (!src) { resolve(); return; }
            fetch(src)
                .then(r => r.blob())
                .then(blob => {
                    const url = URL.createObjectURL(blob);
                    video.setAttribute('src', url);
                    video._blobLoaded = true;
                    if (video.readyState >= 1) {
                        console.log(`✅ Preloader: scrub video ready (${selector})`);
                        resolve();
                    } else {
                        video.addEventListener('loadedmetadata', () => {
                            console.log(`✅ Preloader: scrub video ready (${selector})`);
                            resolve();
                        }, { once: true });
                    }
                })
                .catch(() => resolve());
        });
    };

    const loadLazyVideo = (video) => {
        return new Promise((resolve) => {
            const dataSrc = IS_MOBILE
                ? (video.dataset.srcMobile || video.dataset.src)
                : video.dataset.src;
            if (!dataSrc) { resolve(); return; }

            video.src = dataSrc;
            video.removeAttribute('data-src');
            if (video.dataset.srcMobile) video.removeAttribute('data-src-mobile');
            video.load();

            if (video.hasAttribute('autoplay')) {
                safeAutoplay(video);
            }

            video.addEventListener('canplaythrough', () => {
                console.log(`✅ Preloader: lazy video ready`, video.className || video.id);
                resolve();
            }, { once: true });
            // Per-video fallback timeout
            setTimeout(() => resolve(), 15000);
        });
    };

    const createTimeout = (ms) => {
        return new Promise((resolve) => {
            setTimeout(() => {
                console.warn(`⚠️ Preload timeout after ${ms}ms`);
                resolve('timeout');
            }, ms);
        });
    };

    const hidePreloader = () => {
        if (preloaderHidden || !loader) return;
        preloaderHidden = true;

        gsap.to(loader, {
            opacity: 0,
            duration: 0.8,
            ease: 'power2.out',
            onComplete: () => {
                loader.style.display = 'none';

                // === SCROLL UNLOCK ===
                document.documentElement.style.overflow = '';
                if (window.lenis) window.lenis.start();
                window.scrollTo(0, 0);

                // Start intro video sequence after preloader hides
                if (introManager) {
                    introManager.startIntroSequence();
                }
            }
        });
        console.log('✅ Home preloader hidden');
    };

    // === COLLECT ALL PROMISES ===
    const criticalPromises = [
        waitForIntroVideo(),
        document.fonts.ready,
    ];

    // All planet frames
    if (planetManager && typeof planetManager.getAllFramesPromise === 'function') {
        criticalPromises.push(planetManager.getAllFramesPromise());
    }

    // All build frames
    if (buildManager && typeof buildManager.getAllFramesPromise === 'function') {
        criticalPromises.push(buildManager.getAllFramesPromise());
    }

    // Silencio SVG
    if (window._silencioSvgReady) {
        criticalPromises.push(window._silencioSvgReady);
    }

    // Scrub videos (space + arquitetura) - fetch as blob
    criticalPromises.push(preloadScrubVideo('.space-section__video'));
    criticalPromises.push(preloadScrubVideo('.arquitetura-section__video'));

    // Lazy videos - set src eagerly, wait for canplaythrough
    const lazyVideos = document.querySelectorAll('video[data-src]');
    lazyVideos.forEach(video => {
        criticalPromises.push(loadLazyVideo(video));
    });

    console.log(`🔄 Preloader: aguardando ${criticalPromises.length} recursos críticos...`);

    try {
        await Promise.race([
            Promise.all(criticalPromises),
            createTimeout(MAX_PRELOADER_TIME)
        ]);
        console.log('✅ Preloader: todos os recursos carregados');
    } catch (error) {
        console.warn('⚠️ Preloader: erro ao carregar recursos:', error);
    }

    // Garante tempo mínimo de exibição do preloader para UX
    const elapsedTime = Date.now() - preloaderStartTime;
    const remainingTime = Math.max(0, MIN_PRELOADER_TIME - elapsedTime);

    if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
    }

    hidePreloader();
}


/*---------------------/ 3 - Event Listeners /-------------------*/

/**
 * Aguarda o GSAP estar disponível antes de inicializar
 */
function waitForGSAP(callback, maxAttempts = 50) {
    let attempts = 0;

    const checkGSAP = () => {
        attempts++;

        if (typeof gsap !== 'undefined') {
            console.log('✅ GSAP loaded successfully');
            callback();
        } else if (attempts < maxAttempts) {
            setTimeout(checkGSAP, 100);
        } else {
            console.error('❌ GSAP failed to load after', maxAttempts, 'attempts');
        }
    };

    checkGSAP();
}

document.addEventListener('DOMContentLoaded', () => {
    // Wait for GSAP to be available before initializing
    waitForGSAP(() => {
        // =====================================================
        // CRITICAL SECTIONS - Initialize immediately
        // These are the first 3 sections the user sees
        // =====================================================

        // Initialize intro video manager
        const introManager = new IntroVideoManager();

        // Initialize planet sequence manager (textos + sequência de imagens)
        // Deve ser criado ANTES do preloader para que o preload comece imediatamente
        const planetManager = new PlanetSequenceManager();

        // Initialize build sequence manager (segunda sequência após o planet)
        const buildManager = new BuildSequenceManager(planetManager);

        // =====================================================
        // EARLY PRELOAD - SVG mask for silêncio section
        // Must start BEFORE initPreloader so the promise is available
        // =====================================================
        const silencioSvgUrl = (window.APP_CONFIG ? window.APP_CONFIG.assetPath : '/') + 'site/img/home/silencio.svg';
        window._silencioSvgReady = fetch(silencioSvgUrl)
            .then(r => r.blob())
            .then(blob => {
                window._silencioSvgBlobUrl = URL.createObjectURL(blob);
                console.log('✅ Silencio SVG mask preloaded');
                return true;
            })
            .catch(() => {
                console.warn('⚠️ Silencio SVG mask failed to preload');
                return false;
            });

        // Initialize preloader - aguarda TODOS os recursos antes de liberar a página
        // Bloqueia scroll durante carregamento
        initPreloader(introManager, planetManager, buildManager);

        // =====================================================
        // LAZY SECTIONS - Initialize when approaching viewport
        // These sections are below the fold and can be lazy-loaded
        // =====================================================

        const lazySectionLoader = new LazySectionLoader();

        // Register sections for lazy initialization (300px before viewport)
        lazySectionLoader.register(
            '.space-section',
            () => new SpaceSectionManager(),
            'SpaceSection'
        );

        lazySectionLoader.register(
            '.funciona-section',
            () => new FuncionaSectionManager(),
            'FuncionaSection'
        );

        lazySectionLoader.register(
            '.silencio-section',
            () => new SilencioSectionManager(),
            'SilencioSection'
        );

        lazySectionLoader.register(
            '.arquitetura-section',
            () => new ArquiteturaSectionManager(),
            'ArquiteturaSection'
        );

        lazySectionLoader.register(
            '.essencial-section',
            () => new VideoRevealSectionManager('essencial-section'),
            'EssencialSection'
        );

        lazySectionLoader.register(
            '.permanecer-section',
            () => new VideoRevealSectionManager('permanecer-section'),
            'PermaneceSection'
        );

        lazySectionLoader.register(
            '.rooftop-section',
            () => new VideoRevealSectionManager('rooftop-section'),
            'RooftopSection'
        );

        lazySectionLoader.register(
            '.towers-section',
            () => new TowersSectionManager(),
            'TowersSection'
        );

        // Start observing for lazy initialization
        lazySectionLoader.start();

        // =====================================================
        // LAZY VIDEO LOADING - Load videos when approaching viewport
        // =====================================================

        const lazyVideoLoader = new LazyVideoLoader();
        lazyVideoLoader.init();

        console.log('✅ Home page initialized with lazy section and video loading');
    });
});

